import { useMemo, useRef, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';

import Button from '@/V2/components/ui/button/button';
import { FormField } from '@/V2/components/ui/form/form-field';
import { TextArea } from '@/V2/components/ui/form/textarea';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { z } from 'zod';
import useProjectId from '@/hooks/useProjectId';

import { useGetToneOfVoice } from '../../hooks/use-brand-strategy';
import {
  addToneOfVoice,
  deleteToneOfVoice,
  updateToneOfVoice,
} from '../../services';
import { useBrandStrategyStore } from '../../store/brand-strategy-store';
import { CreateToneOfVoice, ToneOfVoice } from '../../types';
import { hasFormDataChanged } from '../../utils/form-utils';
import { FormGroup } from '../ui/form-group';

const toneOfVoiceSchema = z.object({
  tone_of_voices: z.array(
    z.object({
      id: z.string().nullable(),
      name: z.string().min(1, 'Name is required'),
      description: z.string(),
    })
  ),
});

type ToneOfVoiceFormValues = z.infer<typeof toneOfVoiceSchema>;

export const ToneOfVoiceForm = () => {
  const projectId = useProjectId();
  const { data: toneOfVoiceData, isLoading } = useGetToneOfVoice(projectId);
  const { brandStrategyId } = useBrandStrategyStore();
  const queryClient = useQueryClient();

  // Track which tone of voice forms should be expanded
  const [expandedTones, setExpandedTones] = useState<Set<string>>(new Set());

  // Create stable form data that only changes when tone of voice data actually changes
  const stableFormData = useMemo(() => {
    if (!toneOfVoiceData?.data) {
      return {
        tone_of_voices: [
          {
            id: null,
            name: '',
            description: '',
          },
        ],
      };
    }

    const toneOfVoices = toneOfVoiceData.data.tone_of_voices || [];
    return {
      tone_of_voices:
        toneOfVoices.length > 0
          ? toneOfVoices.map((tone) => ({
              id: tone.id,
              name: tone.name || '',
              description: tone.description || '',
            }))
          : [
              {
                id: null,
                name: '',
                description: '',
              },
            ],
    };
  }, [toneOfVoiceData?.data]);

  const form = useForm<ToneOfVoiceFormValues>({
    resolver: zodResolver(toneOfVoiceSchema),
    defaultValues: stableFormData,
  });

  // Track previous data to handle form synchronization
  const prevDataRef = useRef(stableFormData);

  // Handle form resets when data changes (without useEffect)
  if (JSON.stringify(prevDataRef.current) !== JSON.stringify(stableFormData)) {
    form.reset(stableFormData);
    prevDataRef.current = stableFormData;
  }

  const handleSubmit = form.handleSubmit(async (data) => {
    try {
      await updateStrategy(data);
    } catch (error) {
      console.error('Error in form submission:', error);
    }
  });

  const { fields, append, remove } = useFieldArray({
    name: 'tone_of_voices',
    control: form.control,
  });

  const handleDelete = async (index: number, id: string | null) => {
    if (!id) {
      // If no id, it's a new entry that hasn't been saved yet
      remove(index);

      // Remove from expanded tones
      setExpandedTones((prev) => {
        const newSet = new Set(prev);
        newSet.delete(`new_${index}`);
        return newSet;
      });
      return;
    }

    if (window.confirm('Are you sure you want to delete this tone of voice?')) {
      try {
        if (!brandStrategyId) {
          throw new Error('Brand strategy ID is required');
        }

        const toneOfVoice = form.getValues().tone_of_voices[index];
        if (!toneOfVoice?.id) {
          console.error('No tone of voice ID found for index:', index);
          return;
        }

        // Update the UI immediately
        // Update the store
        const currentBrandStrategy =
          useBrandStrategyStore.getState().brandStrategy;
        if (currentBrandStrategy) {
          const updatedTones = currentBrandStrategy.tone_of_voices.filter(
            (t) => t.id !== toneOfVoice.id
          );
          useBrandStrategyStore.getState().setBrandStrategy({
            ...currentBrandStrategy,
            tone_of_voices: updatedTones,
          });
        }

        // Update the cache
        queryClient.setQueryData(
          ['tone-of-voice', projectId],
          (oldData: any) => {
            if (!oldData) return oldData;
            return {
              ...oldData,
              data: {
                ...oldData.data,
                tone_of_voices: oldData.data.tone_of_voices.filter(
                  (t: ToneOfVoice) => t.id !== toneOfVoice.id
                ),
              },
            };
          }
        );

        // Update the form
        const currentValues = form.getValues();
        form.reset({
          tone_of_voices: currentValues.tone_of_voices.filter(
            (t) => t.id !== toneOfVoice.id
          ),
        });

        // Remove from the form array
        remove(index);

        // Remove from expanded tones
        setExpandedTones((prev) => {
          const newSet = new Set(prev);
          if (toneOfVoice.id) {
            newSet.delete(toneOfVoice.id);
          }
          return newSet;
        });

        try {
          await deleteToneOfVoice(projectId, brandStrategyId, toneOfVoice.id);
          toast.success('Tone of voice deleted successfully');
        } catch (error: any) {
          if (error?.response?.status === 404) {
            console.warn(
              'Tone of voice not found on server, keeping UI update'
            );
          } else {
            if (currentBrandStrategy) {
              useBrandStrategyStore
                .getState()
                .setBrandStrategy(currentBrandStrategy);
            }
            queryClient.invalidateQueries({
              queryKey: ['tone-of-voice', projectId],
            });
            throw error;
          }
        }

        // Invalidate queries
        queryClient.invalidateQueries({
          queryKey: ['brandStrategy', projectId],
        });
      } catch (error) {
        console.error('Error deleting tone of voice:', error);
        toast.error('Failed to delete tone of voice');
      }
    }
  };

  const { mutate: updateStrategy, isPending } = useMutation({
    mutationFn: async (data: ToneOfVoiceFormValues) => {
      if (!brandStrategyId) {
        throw new Error('Brand strategy ID is required');
      }

      // Handle each tone of voice separately
      const results = await Promise.all(
        data.tone_of_voices.map(async (tone) => {
          if (tone.id) {
            // Update existing tone of voice
            const response = await updateToneOfVoice(
              projectId,
              brandStrategyId,
              tone.id,
              {
                id: tone.id,
                name: tone.name,
                description: tone.description,
              }
            );
            return response.data;
          } else {
            // Add new tone of voice
            const newTone: CreateToneOfVoice = {
              name: tone.name,
              description: tone.description,
            };
            const response = await addToneOfVoice(
              projectId,
              brandStrategyId,
              newTone
            );

            // Update expanded tones when new tone gets an ID
            if (response.data?.id) {
              setExpandedTones((prev) => {
                const newSet = new Set(prev);
                // Find the temporary key for this new tone and replace with real ID
                const tempKey = `new_${data.tone_of_voices.findIndex((t) => !t.id)}`;
                newSet.delete(tempKey);
                newSet.add(response.data.id);
                return newSet;
              });
            }

            return response.data;
          }
        })
      );

      // Return the updated tone of voices with their server-assigned IDs
      return {
        tone_of_voices: results,
      };
    },
    onSuccess: (data) => {
      if (!data.tone_of_voices) {
        console.error('No tone of voices in response');
        toast.error('Failed to update tone of voice');
        return;
      }

      // Update the store
      const currentBrandStrategy =
        useBrandStrategyStore.getState().brandStrategy;
      if (currentBrandStrategy) {
        useBrandStrategyStore.getState().setBrandStrategy({
          ...currentBrandStrategy,
          tone_of_voices: data.tone_of_voices,
        });
      }

      // Update the cache more precisely
      queryClient.setQueryData(['tone-of-voice', projectId], (oldData: any) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          data: {
            ...oldData.data,
            tone_of_voices: data.tone_of_voices,
          },
        };
      });

      // Update the form with the server-assigned IDs
      const currentFormValues = form.getValues();
      const updatedFormData = {
        tone_of_voices: data.tone_of_voices.map((tone) => ({
          id: tone.id,
          name: tone.name || '',
          description: tone.description || '',
        })),
      };

      if (hasFormDataChanged(currentFormValues, updatedFormData)) {
        form.reset(updatedFormData);
      }

      toast.success('Tone of voice updated successfully');

      // Only invalidate the main brand strategy query, not the tone-of-voice specific query
      queryClient.invalidateQueries({ queryKey: ['brandStrategy', projectId] });
    },
    onError: (error) => {
      console.error('Error updating tone of voice:', error);
      toast.error('Failed to update tone of voice');
    },
  });

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <div className='flex justify-between items-center mb-6'>
        <div className='flex flex-col'>
          <h2 className='text-xl font-semibold mb-1'>Tone of Voice</h2>
          <p className='text-sm'></p>
        </div>
        <div className='flex mt-2 mr-4'>
          <Button
            type='submit'
            variant='primary'
            size='xsmall'
            onClick={handleSubmit}
            isDisabled={isPending}
          >
            Save
          </Button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className='space-y-8 mr-4'>
        <div className='space-y-6'>
          <div className='space-y-4'>
            {fields.map((field, index) => {
              const tone = form.getValues().tone_of_voices[index];
              const toneKey = tone.id || `new_${index}`;
              const isExpanded = expandedTones.has(toneKey) || index === 0;

              return (
                <FormGroup
                  key={field.id}
                  title={
                    !tone.id
                      ? 'Adding New Tone of Voice'
                      : form.getValues(`tone_of_voices.${index}.name`) ||
                        `Tone of Voice ${index + 1}`
                  }
                  defaultCollapsed={!isExpanded}
                >
                  <div className='space-y-4'>
                    {!tone.id && (
                      <div className='text-sm font-medium text-gray-600 mb-2 italic'>
                        Adding New Tone of Voice
                      </div>
                    )}
                    <FormField
                      label='Tone Name'
                      required
                      error={
                        form.formState.errors.tone_of_voices?.[index]?.name
                          ?.message
                      }
                    >
                      <Controller
                        name={`tone_of_voices.${index}.name`}
                        control={form.control}
                        render={({ field }) => (
                          <input
                            {...field}
                            className='w-full px-3 py-2 border rounded-md'
                            placeholder='Enter tone of voice name...'
                          />
                        )}
                      />
                    </FormField>

                    <FormField
                      label='Description'
                      error={
                        form.formState.errors.tone_of_voices?.[index]
                          ?.description?.message
                      }
                    >
                      <Controller
                        name={`tone_of_voices.${index}.description`}
                        control={form.control}
                        render={({ field }) => (
                          <TextArea
                            placeholder='Describe this tone of voice...'
                            className='min-h-[80px] max-h-[300px] resize-y'
                            {...field}
                          />
                        )}
                      />
                    </FormField>

                    <div className='flex justify-items-start'>
                      {tone.id ? (
                        <button
                          type='button'
                          onClick={() => handleDelete(index, tone.id as string)}
                          className='text-sm underline text-[#E90A04] hover:text-red-700 hover:underline'
                        >
                          Delete
                        </button>
                      ) : (
                        <button
                          type='button'
                          onClick={() => remove(index)}
                          className='p-1 hover:bg-gray-100 text-right rounded-full transition-colors'
                          title='Cancel adding tone of voice'
                        >
                          <X className='w-5 h-5 text-gray-500' />
                        </button>
                      )}
                    </div>
                  </div>
                </FormGroup>
              );
            })}
          </div>

          <div className='flex justify-start'>
            <Button
              type='button'
              variant='secondary'
              size='xsmall'
              onClick={() => {
                const newIndex = fields.length;
                append({
                  id: null,
                  name: '',
                  description: '',
                });

                // Expand the newly added tone of voice form
                setExpandedTones(
                  (prev) => new Set([...prev, `new_${newIndex}`])
                );
              }}
            >
              Add Another Tone of Voice
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};
